/**
 * OAuth Google Ads — redirect + CSRF state (client id público).
 * Stash do modal sobrevive ao remount do React Strict Mode.
 */
import { getSlugFromPage } from '../../utils/routeUtils';

const CLIENT_ID = import.meta.env.VITE_GOOGLE_ADS_CLIENT_ID || '';
const SCOPE = 'https://www.googleapis.com/auth/adwords';
const STATE_KEY = 'adlitics_google_ads_oauth_state';
const ORG_KEY = 'adlitics_google_ads_oauth_org_id';
const PENDING_CODE_KEY = 'adlitics_google_ads_oauth_pending_code';
const MODAL_STASH_KEY = 'adlitics_google_ads_modal';

type StashedCustomer = { customerId: string; name: string };

export class GoogleAdsOAuthRedirectPending extends Error {
    constructor() {
        super('Redirecionando para autenticação Google');
        this.name = 'GoogleAdsOAuthRedirectPending';
    }
}

export const getGoogleAdsOAuthRedirectUri = (): string => {
    const slug = getSlugFromPage('tracking');
    return `${window.location.origin}/${slug}`;
};

export function startGoogleAdsOAuthRedirect(organizationId: string): void {
    if (!CLIENT_ID) {
        throw new Error('Google Ads não configurado (VITE_GOOGLE_ADS_CLIENT_ID).');
    }
    clearGoogleAdsModalStash();
    sessionStorage.removeItem(PENDING_CODE_KEY);
    const state = crypto.randomUUID();
    sessionStorage.setItem(STATE_KEY, state);
    sessionStorage.setItem(ORG_KEY, organizationId);

    const params = new URLSearchParams({
        client_id: CLIENT_ID,
        redirect_uri: getGoogleAdsOAuthRedirectUri(),
        response_type: 'code',
        scope: SCOPE,
        access_type: 'offline',
        prompt: 'consent',
        state,
    });
    window.location.assign(`https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`);
    throw new GoogleAdsOAuthRedirectPending();
}

export function readGoogleAdsOAuthCallback(): { code: string; organizationId: string } | null {
    // Remount do Strict Mode: código já consumido da URL, mas ainda não trocado
    const pendingRaw = sessionStorage.getItem(PENDING_CODE_KEY);
    if (pendingRaw) {
        try {
            const pending = JSON.parse(pendingRaw) as { code: string; organizationId: string };
            if (pending.code && pending.organizationId) return pending;
        } catch {
            sessionStorage.removeItem(PENDING_CODE_KEY);
        }
    }

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    const expected = sessionStorage.getItem(STATE_KEY);
    const orgId = sessionStorage.getItem(ORG_KEY);

    // Mesma rota (/integracoes) serve Meta e Google. Sem state Google salvo,
    // o callback é de outro provider — não toastar "OAuth inválido".
    if (!expected || !orgId) {
        return null;
    }

    if (error) {
        clearGoogleAdsOAuthSession();
        sessionStorage.removeItem(PENDING_CODE_KEY);
        throw new Error(error === 'access_denied' ? 'Autorização Google cancelada.' : `OAuth Google: ${error}`);
    }
    if (!code || !state) return null;

    if (state !== expected) {
        clearGoogleAdsOAuthSession();
        sessionStorage.removeItem(PENDING_CODE_KEY);
        throw new Error('Estado OAuth inválido — tente conectar novamente.');
    }

    clearGoogleAdsOAuthSession();
    const pending = { code, organizationId: orgId };
    sessionStorage.setItem(PENDING_CODE_KEY, JSON.stringify(pending));

    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    url.searchParams.delete('scope');
    window.history.replaceState({}, '', url.pathname + url.search);

    return pending;
}

export function clearGoogleAdsPendingCode(): void {
    sessionStorage.removeItem(PENDING_CODE_KEY);
}

export function stashGoogleAdsModal(customers: StashedCustomer[]): void {
    sessionStorage.setItem(MODAL_STASH_KEY, JSON.stringify({ customers }));
}

export function readGoogleAdsModal(): { customers: StashedCustomer[] } | null {
    const raw = sessionStorage.getItem(MODAL_STASH_KEY);
    if (!raw) return null;
    try {
        const data = JSON.parse(raw) as { customers?: StashedCustomer[] };
        if (!Array.isArray(data.customers)) return null;
        return { customers: data.customers };
    } catch {
        return null;
    }
}

export function clearGoogleAdsModalStash(): void {
    sessionStorage.removeItem(MODAL_STASH_KEY);
}

export function clearGoogleAdsOAuthSession(): void {
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(ORG_KEY);
}
